import { useMemo, type ReactNode } from 'react';
import { ConfigProvider, App as AntApp, Result } from 'antd';
import { MsalProvider } from '@azure/msal-react';
import { buildAntdTheme } from '../shared/theme/antdTheme.ts';
import { useSelector } from 'react-redux';
import type { CommonState } from '../redux/selectors/commonSelectors.ts';
import { AUTH_MODE } from '../services/auth/config.ts';
import { getMsalInstance, isEntraConfigured, isMsalConfigured } from './msalConfig.ts';

// ── Environment ──
const IS_MSAL_AUTH = AUTH_MODE === 'entra' || AUTH_MODE === 'mock-msal' || (AUTH_MODE === 'mock-sso' && isMsalConfigured);

// ── Composed Provider Tree ──

interface ProvidersProps {
  children: ReactNode;
}

/**
 * Wraps the app with providers that live OUTSIDE the router:
 * MSAL (conditionally), Ant Design ConfigProvider (theme-aware),
 * Ant Design App context.
 *
 * Theme is driven by Redux theme state.
 */
export function OuterProviders({ children }: ProvidersProps) {
  const mode = useSelector((state: CommonState) => state.theme.mode);
  const antdTheme = useMemo(() => buildAntdTheme(mode), [mode]);

  const core = (
    <ConfigProvider theme={antdTheme}>
      <AntApp>
        {children}
      </AntApp>
    </ConfigProvider>
  );

  // MSAL is only needed for Entra ID SSO
  if (IS_MSAL_AUTH) {
    if (!isMsalConfigured || (AUTH_MODE === 'entra' && !isEntraConfigured)) {
      return <ConfigProvider theme={antdTheme}>
        <Result status="error" title="Corporate sign-in is unavailable"
          subTitle="The internal portal requires a registered Entra tenant and SPA client. A protected API is required for live authorization; the mockup can run without one." />
      </ConfigProvider>;
    }
    return <MsalProvider instance={getMsalInstance()}>{core}</MsalProvider>;
  }

  return core;
}
